import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Lock, Trophy, Clock, Warning, TrendUp, TrendDown, Lightning } from '@phosphor-icons/react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { showXpToasts } from '../lib/progression';
import { useAuth } from '../context/AuthContext';

const WOLF_IMG = 'https://images.unsplash.com/photo-1564166174574-a9666f590437?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHx3b2xmJTIwaWNvbiUyMGRhcmslMjBhZ2dyZXNzaXZlfGVufDB8fHx8MTc3NjUwMjI4OXww&ixlib=rb-4.1.0&q=85&w=200';

const HYPE_STOCKS = [
  { name: 'MoonShot AI', symbol: 'MOON', sector: 'AI' },
  { name: 'QuantumLeap', symbol: 'QLAP', sector: 'Tech' },
  { name: 'NeuroCoin', symbol: 'NRCN', sector: 'Crypto' },
  { name: 'HyperDrive EV', symbol: 'HDRV', sector: 'EV' },
  { name: 'MetaVerse Corp', symbol: 'MVRS', sector: 'VR' },
  { name: 'RocketFuel Bio', symbol: 'RFBI', sector: 'Biotech' },
  { name: 'GreenStar Energy', symbol: 'GSTR', sector: 'Clean Energy' },
  { name: 'DeepMind Labs', symbol: 'DMLB', sector: 'AI' },
  { name: 'SkyNet Defense', symbol: 'SKNT', sector: 'Defense' },
  { name: 'OceanAI', symbol: 'OCEA', sector: 'AI' },
];

const HYPE_NEWS = [
  '{stock} trending on social media - "To the moon!"',
  'Retail investors pile into {stock} - volume 10x normal',
  '{stock} CEO tweets mysterious rocket emoji',
  'Famous influencer says {stock} is "the next big thing"',
  '{stock} short squeeze incoming says Reddit',
  'Analysts warn {stock} is overvalued - nobody cares',
  'FOMO: {stock} up 300% this week alone',
  '{stock} mentioned on TV - buying frenzy begins',
];

const CRASH_NEWS = [
  'BREAKING: {stock} SEC investigation announced',
  '{stock} CEO dumps millions in shares',
  'Insider report: {stock} revenue is fake',
  '{stock} loses major partnership deal',
  'Short sellers release damning report on {stock}',
];

const getLevelConfig = (level) => {
  // Returns: peakMultiplier, crashSpeed, timeToPlay, hypeSpeed
  if (level <= 10) return { peak: 3 + (level * 0.3), crashSpeed: 0.03, time: 90, hypeSpeed: 6000, startPrice: 10 };
  if (level <= 20) return { peak: 4 + (level * 0.2), crashSpeed: 0.05, time: 75, hypeSpeed: 5000, startPrice: 15 };
  if (level <= 30) return { peak: 3 + (level * 0.15), crashSpeed: 0.07, time: 60, hypeSpeed: 4000, startPrice: 20 };
  if (level <= 40) return { peak: 2.5 + (level * 0.1), crashSpeed: 0.09, time: 50, hypeSpeed: 3500, startPrice: 25 };
  return { peak: 2 + (level * 0.08), crashSpeed: 0.12, time: 45, hypeSpeed: 3000, startPrice: 30 };
};

const WolfMode = () => {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [screen, setScreen] = useState('levels');
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState({ current_level: 1, completed: [], reward_claimed: false });

  // Game state
  const [stock, setStock] = useState(null);
  const [price, setPrice] = useState(10);
  const [history, setHistory] = useState([]);
  const [phase, setPhase] = useState('hype'); // hype, peak, crash
  const [cash, setCash] = useState(10000);
  const [shares, setShares] = useState(0);
  const [avgPrice, setAvgPrice] = useState(0);
  const [timeLeft, setTimeLeft] = useState(90);
  const [news, setNews] = useState([]);
  const [hasSold, setHasSold] = useState(false);
  const gameRef = useRef(null);
  const newsRef = useRef(null);
  const phaseRef = useRef('hype');
  const tickRef = useRef(0);
  const peakTickRef = useRef(0);
  const configRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await api.get('/gamemode/wolf/progress');
      setProgress(res.data.progress || { current_level: 1, completed: [], reward_claimed: false });
    } catch (e) {
      console.warn('[WolfMode] Failed to fetch progress:', e?.message || e);
    }
  }, []);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const startLevel = (lvl) => {
    const config = getLevelConfig(lvl);
    configRef.current = config;
    const s = HYPE_STOCKS[(lvl - 1) % HYPE_STOCKS.length];
    setLevel(lvl);
    setStock(s);
    setPrice(config.startPrice);
    setHistory([{ t: 0, price: config.startPrice }]);
    setPhase('hype');
    phaseRef.current = 'hype';
    tickRef.current = 0;
    peakTickRef.current = 15 + Math.floor(Math.random() * 20); // Random peak between tick 15-35
    setCash(10000);
    setShares(0);
    setAvgPrice(0);
    setTimeLeft(config.time);
    setNews([]);
    setHasSold(false);
    setScreen('playing');
  };

  // Price simulation
  useEffect(() => {
    if (screen !== 'playing') return;
    const config = configRef.current;

    gameRef.current = setInterval(() => {
      tickRef.current++;
      const t = tickRef.current;

      setPrice(prev => {
        let np;
        if (phaseRef.current === 'hype') {
          // Exponential rise
          const growth = 1 + (0.02 + Math.random() * 0.04);
          np = prev * growth;
          if (t >= peakTickRef.current) {
            phaseRef.current = 'peak';
            setPhase('peak');
          }
        } else if (phaseRef.current === 'peak') {
          // Brief plateau (2-5 ticks)
          np = prev * (1 + (Math.random() - 0.5) * 0.01);
          if (t >= peakTickRef.current + 3) {
            phaseRef.current = 'crash';
            setPhase('crash');
          }
        } else {
          // Crash
          const drop = 1 - (config.crashSpeed + Math.random() * 0.03);
          np = Math.max(0.5, prev * drop);
        }
        setHistory(h => [...h, { t, price: np }].slice(-80));
        return np;
      });

      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(gameRef.current);
          clearInterval(newsRef.current);
          setScreen('result');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(gameRef.current);
  }, [screen]);

  // News
  useEffect(() => {
    if (screen !== 'playing' || !stock) return;
    const config = configRef.current;

    const gen = () => {
      const isCrash = phaseRef.current === 'crash';
      const pool = isCrash ? CRASH_NEWS : HYPE_NEWS;
      const tmpl = pool[Math.floor(Math.random() * pool.length)];
      setNews(prev => [{ id: Date.now(), text: tmpl.replace('{stock}', stock.symbol), isCrash, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 6));
    };

    setTimeout(gen, 2000);
    newsRef.current = setInterval(gen, config.hypeSpeed);
    return () => clearInterval(newsRef.current);
  }, [screen, stock]);

  const handleBuy = () => {
    if (cash <= 0) return;
    const buyAmount = Math.min(cash, cash * 0.5);
    const buyShares = Math.floor(buyAmount / price);
    if (buyShares <= 0) return;
    const cost = buyShares * price;
    const newTotal = shares + buyShares;
    setAvgPrice(shares > 0 ? ((shares * avgPrice) + cost) / newTotal : price);
    setShares(newTotal);
    setCash(p => p - cost);
  };

  const handleSell = () => {
    if (shares <= 0) return;
    setCash(p => p + shares * price);
    setShares(0);
    setAvgPrice(0);
    setHasSold(true);
  };

  const getTotal = () => cash + (shares * price);

  // Save on result
  const savedRef = useRef(false);
  useEffect(() => {
    if (screen === 'result' && !savedRef.current) {
      savedRef.current = true;
      const total = getTotal();
      const ret = ((total - 10000) / 10000) * 100;
      if (ret >= 5) {
        api.post('/gamemode/wolf/complete-level', { level }).then(res => {
          if (res.data.reward > 0) toast.success(`All 50 levels complete! +$${res.data.reward.toLocaleString()} to portfolio!`);
          showXpToasts(res.data.xp_result, { refreshUser: fetchUser });
          fetchProgress();
        }).catch((e) => {
          console.warn('[WolfMode] complete-level failed:', e?.message || e);
        });
      }
    }
    if (screen !== 'result') savedRef.current = false;
  }, [screen]);

  // =================== LEVELS SCREEN ===================
  if (screen === 'levels') {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/game-modes')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} /> Back to Game Modes
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-5 mb-8">
            <img src={WOLF_IMG} alt="Wolf" className="w-20 h-20 rounded-2xl object-cover" />
            <div>
              <h1 className="text-3xl font-unbounded font-bold text-white">Wolf Mode</h1>
              <p className="text-zinc-400">Ride the hype. Time your exit. Don't get caught in the crash.</p>
              <p className="text-zinc-500 text-sm mt-1">{progress.completed?.length || 0}/50 — Complete all for <span className="text-gold font-bold">+$3,000</span></p>
            </div>
          </motion.div>

          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-6">
            {Array.from({ length: 50 }, (_, i) => i + 1).map(lvl => {
              const done = progress.completed?.includes(lvl);
              const unlocked = lvl <= (progress.current_level || 1);
              const isCurrent = lvl === (progress.current_level || 1) && !done;
              return (
                <motion.button key={lvl} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: lvl * 0.01 }}
                  disabled={!unlocked} onClick={() => unlocked && startLevel(lvl)}
                  className={`aspect-square rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                    done ? 'bg-red-500/20 border-2 border-red-500 text-red-400' :
                    isCurrent ? 'bg-red-500/20 border-2 border-red-500 text-red-400 animate-pulse' :
                    unlocked ? 'bg-zinc-800 border border-zinc-700 text-white hover:border-red-500/50' :
                    'bg-zinc-900/30 border border-zinc-800/50 text-zinc-700'
                  }`}
                  data-testid={`wolf-level-${lvl}`}
                >
                  {done ? <CheckCircle size={18} weight="fill" /> : !unlocked ? <Lock size={14} /> : lvl}
                </motion.button>
              );
            })}
          </div>

          <div className="glass-card p-5 border-l-4 border-l-red-500">
            <p className="text-zinc-400 text-sm">Target: <span className="text-white font-bold">+5% profit</span> per level. Buy during the hype, sell before the crash!</p>
          </div>
        </div>
      </div>
    );
  }

  // =================== PLAYING ===================
  if (screen === 'playing' && stock) {
    const total = getTotal();
    const ret = ((total - 10000) / 10000) * 100;
    const chartColor = phase === 'crash' ? '#ef4444' : phase === 'peak' ? '#eab308' : '#10b981';

    return (
      <div className="min-h-screen noise-bg">
        <div className="max-w-5xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${phase === 'crash' ? 'bg-red-500/20 text-red-400' : phase === 'peak' ? 'bg-yellow-500/20 text-yellow-400 animate-pulse' : 'bg-emerald-500/20 text-emerald-400'}`}>
                  {phase === 'crash' ? 'CRASHING' : phase === 'peak' ? 'AT PEAK' : 'HYPE RISING'}
                </span>
                <h2 className="text-lg font-bold text-white">Wolf Mode — Level {level}</h2>
              </div>
              <p className="text-zinc-500 text-sm">{stock.name} ({stock.symbol}) — {stock.sector}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-zinc-500 text-xs">Portfolio</p>
                <p className={`font-bold ${ret >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${total.toFixed(0)} ({ret >= 0 ? '+' : ''}{ret.toFixed(1)}%)</p>
              </div>
              <div className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${timeLeft <= 15 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-zinc-800 text-white'}`}>
                <Clock size={16} /><span className="font-mono font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Chart */}
            <div className="lg:col-span-2 bg-zinc-900/50 rounded-2xl p-5 border border-zinc-800">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-3xl font-bold text-white">${price.toFixed(2)}</p>
                  <p className={`text-sm ${price > (configRef.current?.startPrice || 10) ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(((price - (configRef.current?.startPrice || 10)) / (configRef.current?.startPrice || 10)) * 100).toFixed(1)}% from start
                  </p>
                </div>
                {shares > 0 && (
                  <div className="bg-yellow-500/10 px-3 py-2 rounded-lg text-right">
                    <p className="text-yellow-400 text-sm font-bold">{shares} shares</p>
                    <p className="text-zinc-400 text-xs">P/L: ${((price - avgPrice) * shares).toFixed(0)}</p>
                  </div>
                )}
              </div>

              <div className="h-48 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history}>
                    <XAxis dataKey="t" hide />
                    <YAxis domain={['auto', 'auto']} hide />
                    <Area type="monotone" dataKey="price" stroke={chartColor} fill={chartColor} fillOpacity={0.1} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleBuy} disabled={cash < price} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-lg font-bold" data-testid="wolf-buy-btn">
                  <TrendUp size={20} className="mr-2" /> BUY
                </Button>
                <Button onClick={handleSell} disabled={shares <= 0} className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-lg font-bold" data-testid="wolf-sell-btn">
                  <TrendDown size={20} className="mr-2" /> SELL ALL
                </Button>
              </div>

              <div className="flex justify-between mt-3 text-sm text-zinc-500">
                <span>Cash: ${cash.toFixed(0)}</span>
                <span>{shares} shares @ avg ${avgPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* News feed */}
            <div className="bg-zinc-900/50 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <Lightning size={18} className="text-yellow-400" />
                <h3 className="text-white font-bold text-sm">Market Buzz</h3>
              </div>
              <div className="space-y-2">
                {news.map(n => (
                  <motion.div key={n.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    className={`p-2.5 rounded-lg text-xs ${n.isCrash ? 'bg-red-500/10 border border-red-500/30' : 'bg-emerald-500/10 border border-emerald-500/20'}`}
                  >
                    <p className="text-white">{n.text}</p>
                    <p className="text-zinc-500 mt-0.5">{n.time}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =================== RESULT ===================
  if (screen === 'result') {
    const total = getTotal();
    const ret = ((total - 10000) / 10000) * 100;
    const passed = ret >= 5;

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
            <div className={`inline-flex p-6 rounded-full mb-6 ${passed ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              {passed ? <Trophy size={64} weight="fill" className="text-emerald-400" /> : <Warning size={64} weight="fill" className="text-red-400" />}
            </div>
            <h1 className="text-3xl font-unbounded font-bold text-white mb-2">
              {passed ? 'You Escaped!' : 'Caught in the Crash!'}
            </h1>
            <p className="text-zinc-400 mb-8">
              {passed ? `Smart exit! You made ${ret.toFixed(1)}% profit.` : `You needed +5% but got ${ret.toFixed(1)}%. Time your exit better!`}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Final Value</p>
                <p className="text-xl font-bold text-white">${total.toFixed(0)}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Return</p>
                <p className={`text-xl font-bold ${ret >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{ret >= 0 ? '+' : ''}{ret.toFixed(1)}%</p>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={() => startLevel(level)} className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700">
                {passed ? 'Replay' : 'Retry'} Level {level}
              </Button>
              {passed && level < 50 ? (
                <Button onClick={() => startLevel(level + 1)} className="flex-1 py-4 bg-gradient-to-r from-red-500 to-orange-600 font-bold" data-testid="wolf-next">
                  Next Level <ArrowRight size={18} className="ml-2" />
                </Button>
              ) : (
                <Button onClick={() => setScreen('levels')} className="flex-1 py-4 bg-red-600 hover:bg-red-700">Back to Levels</Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default WolfMode;
